import { Alert, Badge, TextTooltip } from '@components'
import { BusinessForms } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaCheckCircle, FaFileAlt, FaMinus, FaRegCircle } from 'react-icons/fa'
import styled from 'styled-components'
import {
  AutoEntries,
  Bullet,
  EntryPill,
  ExampleDate,
  ExampleDates,
  ExampleHeader,
  ExamplePanel,
  Eyebrow,
  Intro,
  MethodLabel,
  MethodNote,
  MethodRow,
  OptionCard,
  OptionDescription,
  OptionGrid,
  OptionHeader,
  PillCell,
  RecommendationBadge
} from './AccrualAccountingModal.styled'

export type AccrualChoice = 'yes' | 'no'

export const getRecommendedAccrualChoice = (form?: BusinessForms): AccrualChoice | null => {
  if (form === BusinessForms.FI_OY) return 'yes'
  if (form === BusinessForms.FI_TMI) return 'no'
  return null
}

interface Props {
  value: AccrualChoice
  onChange: (value: AccrualChoice) => void
  businessForm?: BusinessForms
  recommended?: AccrualChoice | null
  /** When false, hide the intro paragraph (hub tab provides its own helper). */
  showIntro?: boolean
  introText?: string
}

export const AccountingMethodSelector: React.FC<Props> = ({
  value,
  onChange,
  businessForm,
  recommended = null,
  showIntro = false,
  introText
}) => {
  const [t] = useTranslation()

  return (
    <>
      {showIntro && introText && <Intro>{introText}</Intro>}

      <ExamplePanel>
        <ExampleHeader>
          <Eyebrow>{t('dashboard.getStartedWidget.accrualAccounting.example.eyebrow')}</Eyebrow>
          <span>{t('dashboard.getStartedWidget.accrualAccounting.example.question')}</span>
        </ExampleHeader>
        <ExampleDates>
          <span />
          <ExampleDate>
            <strong>
              {t('dashboard.getStartedWidget.accrualAccounting.example.workDoneDate')}
            </strong>
            <span>{t('dashboard.getStartedWidget.accrualAccounting.example.workDone')}</span>
          </ExampleDate>
          <ExampleDate>
            <strong>{t('dashboard.getStartedWidget.accrualAccounting.example.paidDate')}</strong>
            <span>{t('dashboard.getStartedWidget.accrualAccounting.example.paid')}</span>
          </ExampleDate>
        </ExampleDates>
        <MethodRow $selected={value === 'yes'} onClick={() => onChange('yes')}>
          <MethodLabel>{t('dashboard.getStartedWidget.accrualAccounting.yes.title')}</MethodLabel>
          <PillCell>
            <TextTooltip
              tooltip={t('dashboard.getStartedWidget.accrualAccounting.example.accrualTooltip')}
            >
              <EntryPill>
                <FaFileAlt />
                {t('dashboard.getStartedWidget.accrualAccounting.example.accrualEntry')}
              </EntryPill>
            </TextTooltip>
          </PillCell>
          <MethodNote>
            {t('dashboard.getStartedWidget.accrualAccounting.example.accrualSettle')}
          </MethodNote>
        </MethodRow>
        <MethodRow $selected={value === 'no'} onClick={() => onChange('no')}>
          <MethodLabel>{t('dashboard.getStartedWidget.accrualAccounting.no.title')}</MethodLabel>
          <MethodNote>
            {t('dashboard.getStartedWidget.accrualAccounting.example.cashNone')}
          </MethodNote>
          <PillCell>
            <TextTooltip
              tooltip={t('dashboard.getStartedWidget.accrualAccounting.example.cashTooltip')}
            >
              <EntryPill>
                <FaFileAlt />
                {t('dashboard.getStartedWidget.accrualAccounting.example.cashEntry')}
              </EntryPill>
            </TextTooltip>
          </PillCell>
        </MethodRow>
      </ExamplePanel>

      <OptionGridWithBadgePadding
        role="radiogroup"
        aria-label={t('dashboard.getStartedWidget.accrualAccounting.title')}
      >
        {(['yes', 'no'] as const).map(choice => (
          <OptionCard
            key={choice}
            role="radio"
            tabIndex={0}
            aria-checked={value === choice}
            $selected={value === choice}
            onClick={() => onChange(choice)}
            onKeyDown={event => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                onChange(choice)
              }
            }}
          >
            {recommended === choice && (
              <RecommendationBadge>
                <Badge type="success" soft>
                  {t(
                    businessForm === BusinessForms.FI_OY
                      ? 'dashboard.getStartedWidget.accrualAccounting.recommendedOy'
                      : 'dashboard.getStartedWidget.accrualAccounting.recommendedTmi'
                  )}
                </Badge>
              </RecommendationBadge>
            )}
            <OptionHeader $selected={value === choice}>
              {value === choice ? <FaCheckCircle /> : <FaRegCircle />}
              {t(`dashboard.getStartedWidget.accrualAccounting.${choice}.title`)}
            </OptionHeader>
            <OptionDescription>
              {t(`dashboard.getStartedWidget.accrualAccounting.${choice}.description`)}
            </OptionDescription>
            <AutoEntries>
              <Eyebrow>{t('dashboard.getStartedWidget.accrualAccounting.autoEntries')}</Eyebrow>
              <Bullet>
                <FaCheck />
                {t(`dashboard.getStartedWidget.accrualAccounting.${choice}.bullet1`)}
              </Bullet>
              <Bullet $muted={choice === 'no'}>
                {choice === 'yes' ? <FaCheck /> : <FaMinus />}
                {t(`dashboard.getStartedWidget.accrualAccounting.${choice}.bullet2`)}
              </Bullet>
            </AutoEntries>
          </OptionCard>
        ))}
      </OptionGridWithBadgePadding>

      {businessForm === BusinessForms.FI_OY && value === 'no' && (
        <Alert
          type="info"
          marginBottom={0}
          title={t('dashboard.getStartedWidget.accrualAccounting.oyCashNote.title')}
          description={t('dashboard.getStartedWidget.accrualAccounting.oyCashNote.description')}
        />
      )}
    </>
  )
}

const OptionGridWithBadgePadding = styled(OptionGrid)`
  padding-top: ${({ theme }) => theme.spacing.xs}rem;
`
